import { HttpErrorResponse } from '@angular/common/http';
import { Component, computed, CUSTOM_ELEMENTS_SCHEMA, inject, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import {
  Beneficio,
  ETAPA_TERMO,
  ParticipanteHome,
  RegimeIr,
  Requerimento,
  Resumo,
  ResumoValores,
  SaldosRequerimento,
  TributacaoSimulada,
} from '../../../core/models/requerimento';
import {
  toBeneficioApi,
  toCadastroPayload,
  toTributacaoPayload,
} from '../../../core/models/requerimento-mapper';
import { Steps } from '../../../shared/components/steps/steps';
import { LoaderService } from '../../../shared/services/loader-service';
import { RequerimentoService } from '../../../shared/services/requerimento-service';
import { RequerimentoState } from '../../../shared/services/requerimento-state';
import { ToastService } from '../../../shared/services/toast-service';
import { formatarAliquota, formatarValor } from '../../../shared/utils/formatadores';
import {
  AntecipacaoResumo,
  calcularAntecipacao,
  formaDeRecebimento,
  valorBrutoAntecipacao,
} from '../../../shared/utils/beneficio-resumo';
import {
  distribuirErrosDaApi,
  ROTA_PAGINA_INDISPONIVEL,
  rotaDoErroRequerimento,
} from '../../../shared/utils/requerimento-erros';

/** Linha das tabelas: rótulo à esquerda e valor à direita. */
interface LinhaResumo {
  rotulo: string;
  valor: string;
}


// TODO: o simulador recebe o percentual de antecipação, mas não devolve o IR dela; a alíquota abaixo é só para exibir
// a antecipação. Substituir quando o backend devolver o IR da antecipação.
export const ALIQUOTA_IR_ANTECIPACAO_MOCK = 15;

const MENSAGEM_ETAPAS_INCOMPLETAS = 'Conclua as etapas anteriores antes de seguir para o termo.';
const MENSAGEM_ERRO_SALVAR = 'Não foi possível confirmar os dados. Tente novamente.';

/** Dado ainda não informado no requerimento. */
const SEM_VALOR = '-';

const NOME_DO_REGIME: Record<RegimeIr, string> = { P: 'Progressivo', R: 'Regressivo' };

function simNao(valor: boolean | null | undefined): string {
  if (valor === undefined || valor === null) return SEM_VALOR;
  return valor ? 'Sim' : 'Não';
}

function formatarCpf(cpf: string | null): string {
  const digitos = cpf?.replace(/\D/g, '') ?? '';
  if (digitos === '') return SEM_VALOR;
  return digitos.padStart(11, '0').replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
}

/** Arredonda para a escala da coluna do resumo (NUMBER(18,2) nos valores, NUMBER(7,4) na alíquota). */
function arredondar(valor: number, casas: number): number {
  const fator = 10 ** casas;
  return Math.round(valor * fator) / fator;
}

/**
 * Snapshot da etapa 6: saldos do GET /saldos; bruto, alíquota, IR e líquido do regime escolhido da simulação de IR;
 * antecipação e tempo estimado do benefício da etapa 2 (a antecipação é o percentual sobre o saldo geral).
 */
function montarResumo(
  simulacao: TributacaoSimulada,
  saldos: SaldosRequerimento,
  regime: RegimeIr,
  beneficio: Beneficio | null,
): ResumoValores | null {
  const simulado = simulacao.regimes.find((item) => item.regime === regime);
  if (!simulado) return null;

  const saldoParticipante = arredondar(saldos.saldoParticipante, 2);
  const saldoPatrocinadora = arredondar(saldos.saldoPatrocinadora, 2);
  const percentualAntecipacao = beneficio?.percentualAntecipacaoSaldo ?? 0;

  return {
    saldoParticipante,
    saldoPatrocinadora,
    percentualAntecipacao,
    valorAntecipacao: valorBrutoAntecipacao(percentualAntecipacao, saldoParticipante + saldoPatrocinadora),
    valorBrutoMensal: arredondar(simulacao.valorBruto, 2),
    percentualImpostoRenda: arredondar(simulado.aliquotaEfetiva, 4),
    valorImpostoRenda: arredondar(simulado.valorIr, 2),
    valorLiquidoMensal: arredondar(simulado.valorLiquido, 2),
    // Obrigatório na API (≥ 0, 1 casa). Só o Prazo Certo tem prazo; nas outras modalidades vai 0.
    tempoEstimadoAnos: arredondar(beneficio?.qtdAnosPrazo ?? 0, 1),
    // Parcelas e resgate são do instituto pago em parcelas (tela 10), fora do requerimento de benefício.
    qtdParcelas: null,
    valorPrimeiraParcela: null,
    dataPrimeiraParcela: null,
    valorBrutoResgate: null,
    valorImpostoRendaResgate: null,
    valorLiquidoResgate: null,
    retornoCalculo: simulacao.retorno,
  };
}

@Component({
  imports: [Steps],
  selector: 'app-resumo-requerimento',
  styleUrl: './resumo-requerimento.scss',
  templateUrl: './resumo-requerimento.html',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ResumoRequerimento implements OnInit {
  private readonly router = inject(Router);
  private readonly loaderService = inject(LoaderService);
  private readonly requerimentoService = inject(RequerimentoService);
  private readonly requerimentoState = inject(RequerimentoState);
  private readonly toastService = inject(ToastService);

  readonly stepAtual = signal(6);
  readonly totalSteps = signal(7);

  readonly requerimento = signal<Requerimento | null>(null);
  /**
   * Snapshot exibido: o GET /resumo depois de gravar o recálculo feito ao abrir a tela. Com o termo já aceito, ou sem
   * como recalcular, é o snapshot que já estava gravado.
   */
  readonly valores = signal<Resumo | null>(null);

  /** Nome e CPF não vêm no requerimento: são os do participante do GET /v1/requerimentos/home. */
  readonly participante = signal<ParticipanteHome | null>(null);

  readonly dadosPessoais = computed<LinhaResumo[]>(() => {
    const requerimento = this.requerimento();
    const participante = this.participante();
    const regime = requerimento?.tributacao?.regime;

    return [
      { rotulo: 'Nome:', valor: participante?.nome?.trim() || SEM_VALOR },
      { rotulo: 'CPF:', valor: formatarCpf(participante?.cpf ?? null) },
      { rotulo: 'Moléstia Grave', valor: simNao(requerimento?.cadastro?.molestiaGrave) },
      { rotulo: 'Imposto de Renda:', valor: regime ? NOME_DO_REGIME[regime] : SEM_VALOR },
      { rotulo: 'Forma de recebimento:', valor: formaDeRecebimento(requerimento?.beneficio) },
    ];
  });

  readonly dadosRecebimento = computed<LinhaResumo[]>(() => {
    const cadastro = this.requerimento()?.cadastro;
    const beneficio = this.requerimento()?.beneficio;
    const percentualAntecipacao = beneficio?.percentualAntecipacaoSaldo;

    return [
      { rotulo: 'Adiantamento de benefício', valor: simNao(beneficio?.adiantaBeneficio) },
      {
        rotulo: 'Antecipação de saldo à vista',
        valor: percentualAntecipacao == null ? SEM_VALOR : `${formatarAliquota(percentualAntecipacao)}%`,
      },
      {
        rotulo: 'Banco',
        // Nome gravado pela API; sem ele, o código do banco.
        valor:
          cadastro?.nomeBanco ||
          (cadastro?.codBanco == null ? SEM_VALOR : String(cadastro.codBanco).padStart(3, '0')),
      },
      { rotulo: 'Agência', valor: cadastro?.agencia ?? SEM_VALOR },
      { rotulo: 'Conta', valor: cadastro?.conta ?? SEM_VALOR },
    ];
  });

  /** Saldo geral: conta de contribuição do participante mais a da patrocinadora. */
  readonly saldoGeral = computed(() => {
    const valores = this.valores();
    return valores ? valores.saldoParticipante + valores.saldoPatrocinadora : 0;
  });

  /** Só existe com antecipação escolhida na etapa 2 (percentual maior que 0). */
  readonly antecipacao = computed<AntecipacaoResumo | null>(() => calcularAntecipacao(this.valores(), ALIQUOTA_IR_ANTECIPACAO_MOCK));

  protected readonly formatarValor = formatarValor;
  /** Percentuais e anos sem casas fixas (15 → "15"; 12.5 → "12,5"). */
  protected readonly formatarNumero = formatarAliquota;

  async ngOnInit(): Promise<void> {
    this.loaderService.showLoader('Carregando...');
    const participante = this.carregarParticipante();
    try {
      await participante;
      const requerimento = await this.requerimentoState.carregar();
      this.requerimento.set(requerimento);
      // O GET só depois do POST/PUT do recálculo; com o termo já aceito, só o GET do snapshot congelado.
      const termoAceito = requerimento.etapaAtual >= ETAPA_TERMO;
      if (termoAceito || (await this.gravarResumo(requerimento))) {
        this.valores.set(await this.requerimentoService.getResumo(requerimento.id));
      }
    } catch (erro) {
      this.router.navigate([rotaDoErroRequerimento(erro) ?? ROTA_PAGINA_INDISPONIVEL]);
    } finally {
      this.loaderService.hideLoader();
    }
  }

  /** Sem a home, nome e CPF ficam "-" e o resumo segue: os erros de acesso aparecem no carregamento do requerimento. */
  private async carregarParticipante(): Promise<void> {
    try {
      this.participante.set((await this.requerimentoState.carregarHome()).participante);
    } catch {
      this.participante.set(null);
    }
  }

  onReturn(): void {
    this.router.navigate(['/requerimento-beneficio/upload-documento']);
  }

  async onSubmit(): Promise<void> {
    this.loaderService.showLoader('Salvando...');
    try {
      const requerimento = await this.requerimentoState.carregar();
      if (requerimento.etapaAtual >= ETAPA_TERMO) {
        // Termo já aceito (o envio falhou depois): o PUT invalidaria o aceite sem nada ter mudado (PRE-275).
        this.router.navigate(['/requerimento-beneficio/assinatura-termo']);
        return;
      }

      const { cadastro, beneficio, tributacao } = requerimento;
      if (!cadastro || !beneficio || !tributacao) {
        this.toastService.show(MENSAGEM_ETAPAS_INCOMPLETAS, 'error');
        this.router.navigate(['/requerimento-beneficio']);
        return;
      }

      const atualizado = await this.requerimentoService.substituir(requerimento.id, {
        cadastro: toCadastroPayload(cadastro),
        beneficio: toBeneficioApi(beneficio),
        tributacao: toTributacaoPayload(tributacao),
      });
      this.requerimentoState.definir(atualizado);
      this.router.navigate(['/requerimento-beneficio/assinatura-termo']);
    } catch (erro) {
      this.tratarErroAoSalvar(erro);
    } finally {
      this.loaderService.hideLoader();
    }
  }

  /** Recalcula com o GET /saldos e a simulação de IR da API; null sem regime de IR escolhido. */
  private async recalcular(requerimento: Requerimento): Promise<ResumoValores | null> {
    const regime = requerimento.tributacao?.regime;
    if (!regime) return null;

    const [saldos, simulacao] = await Promise.all([
      this.requerimentoService.getSaldos(requerimento.id),
      this.requerimentoService.simularTributacao(requerimento.id),
    ]);
    return montarResumo(simulacao, saldos, regime, requerimento.beneficio);
  }

  /**
   * POST do recálculo; se o snapshot já existe (409), PUT. Devolve false sem regime de IR (não há o que gravar); os
   * erros seguem o padrão das etapas.
   */
  private async gravarResumo(requerimento: Requerimento): Promise<boolean> {
    const valores = await this.recalcular(requerimento);
    if (!valores) return false;

    try {
      await this.requerimentoService.criarResumo(requerimento.id, valores);
    } catch (erro) {
      if (!(erro instanceof HttpErrorResponse && erro.status === 409)) throw erro;
      await this.requerimentoService.substituirResumo(requerimento.id, valores);
    }
    return true;
  }

  private tratarErroAoSalvar(erro: unknown): void {
    const rota = rotaDoErroRequerimento(erro);
    if (rota) {
      this.router.navigate([rota]);
      return;
    }

    distribuirErrosDaApi(erro, {}, MENSAGEM_ERRO_SALVAR).forEach((mensagem) =>
      this.toastService.show(mensagem, 'error'),
    );
  }
}
